import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/api';
import { RefreshCw, ShieldCheck, Download, UserCheck } from 'lucide-react';

const API_URL = getApiUrl();

/**
 * Data Repair Card — escape-hatch maintenance actions for OPS admins.
 * All are idempotent (safe to re-run). The dashboards self-correct via webhooks;
 * these cover the rare drift cases.
 *
 *   1. Manual Integrity Reconcile — Razorpay ↔ OPS audit (same as daily cron).
 *   2. V36.3 · Sync Razorpay Direct Captures — pull dashboard-direct payments.
 *   3. V36.8 · Heal Lead + Booking Assignments — enforce owner invariant.
 *   4. V42.5 · Fix Enrolled Lead Status — flip paid customers to "enrolled".
 */
const DataRepairCard = () => {
  const [running, setRunning] = useState(null);
  const [reconcileResult, setReconcileResult] = useState(null);
  // V36.8 — assignment heal (lead → pre-sales, booking → sales)
  const [assignResult, setAssignResult] = useState(null);
  const [plus1Result, setPlus1Result] = useState(null);
  // V36.3 — Razorpay direct sync
  const [rzpSyncDays, setRzpSyncDays] = useState(30);
  const [rzpSyncResult, setRzpSyncResult] = useState(null);
  // V42.5 — backfill enrolled lead_status (fix WIP showing for paid customers)
  const [leadStatusResult, setLeadStatusResult] = useState(null);
  // One-shot phone normalization (numeric / formatted phones break lead search)
  const [phoneResult, setPhoneResult] = useState(null);
  // V57.2 — India funnel / INR-paid leads wrongly tagged International
  const [inFunnelResult, setInFunnelResult] = useState(null);

  const runHealInFunnel = async (dryRun) => {
    if (!dryRun && !window.confirm(
      'Set country = India for leads from soulsanskar.in or who paid in INR but are tagged "International"?\n\n' +
      'The corrected country is also pushed to the LMS so its finance audit flags clear. A real foreign country is never overwritten. Idempotent — safe to re-run.'
    )) return;
    setRunning(dryRun ? 'infunnel_dryrun' : 'infunnel');
    setInFunnelResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/data-repair/heal-in-funnel-country`, { dry_run: !!dryRun }, {
        withCredentials: true, timeout: 300000,
      });
      setInFunnelResult(res.data);
      toast[dryRun ? 'info' : 'success'](
        dryRun ? `Dry-run · ${res.data.fixed} lead(s) would be set to India`
               : `Fixed ${res.data.fixed} lead(s) → India · ${res.data.lms_pushed} pushed to LMS`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'India country heal failed');
    } finally {
      setRunning(null);
    }
  };

  const runNormalizePhones = async (dryRun) => {
    if (!dryRun && !window.confirm(
      'Rewrite every lead phone into a clean string and backfill normalized_phone?\n\n' +
      'Fixes leads that cannot be found by phone search (numbers stored as numeric or with spaces). Idempotent — safe to re-run.'
    )) return;
    setRunning(dryRun ? 'phones_dryrun' : 'phones');
    setPhoneResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/data-repair/normalize-lead-phones`, { dry_run: !!dryRun }, {
        withCredentials: true, timeout: 300000,
      });
      setPhoneResult(res.data);
      toast[dryRun ? 'info' : 'success'](
        `${dryRun ? 'Dry-run · would fix' : 'Fixed'} ${res.data.total_changed} field(s) across ${res.data.scanned} lead(s)`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Phone normalization failed');
    } finally {
      setRunning(null);
    }
  };

  const runReconcile = async () => {
    setRunning('reconcile');
    setReconcileResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/data-repair/reconcile-yesterday`, {}, {
        withCredentials: true, timeout: 180000,
      });
      setReconcileResult(res.data);
      if (res.data.has_issues) {
        toast.warning(`Integrity issues found — see results below`);
      } else {
        toast.success(`All clean — ${res.data.ops_payments_count} OPS payments reconciled against ${res.data.razorpay_payments_count} Razorpay payments`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reconcile failed');
    } finally {
      setRunning(null);
    }
  };

  // V36.3 — Pull payments straight from Razorpay's API (for direct dashboard
  // captures that bypassed OPS webhooks). Two-step UX:
  //   1. Dry-run → previews how many WOULD be created
  //   2. Live sync → actually inserts/updates
  const runRzpSync = async (dryRun) => {
    const days = Math.max(1, Math.min(parseInt(rzpSyncDays || 30, 10), 365));
    if (!dryRun) {
      if (!window.confirm(
        `Pull live payments from Razorpay API for the last ${days} day(s) and insert/update them into OPS?\n\n` +
        `This is idempotent — re-running with the same window is safe.\n` +
        `Each payment is keyed by Razorpay payment_id, so duplicates are not created.\n\n` +
        `Best-effort lead relink via email/phone. Continue?`
      )) return;
    }
    setRunning(dryRun ? 'rzp_dryrun' : 'rzp_sync');
    setRzpSyncResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/data-repair/sync-razorpay-direct`,
        { days, dry_run: !!dryRun },
        { withCredentials: true, timeout: 300000 }
      );
      setRzpSyncResult({ ...res.data, _ran_at: new Date().toISOString() });
      if (dryRun) {
        toast.info(
          `Dry-run · fetched ${res.data.fetched_from_razorpay}, would create ${res.data.payments_created}`
        );
      } else {
        toast.success(
          `Sync done · fetched ${res.data.fetched_from_razorpay}, created ${res.data.payments_created}, updated ${res.data.payments_updated}`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Razorpay sync failed');
    } finally {
      setRunning(null);
    }
  };

  // V42.5 — Backfill lead_status='enrolled' for paid customers stuck on wip/etc.
  const runBackfillEnrolledStatus = async (dryRun) => {
    if (!dryRun && !window.confirm(
      "Set lead_status to 'Enrolled' for every paid customer (enrollment_status enrolled/renewed) whose status is still WIP or another pipeline stage?\n\n" +
      'Fixes the Sales "Enrolled Leads" list showing WIP for customers who already paid. Idempotent — safe to re-run. Run the dry-run first to preview.'
    )) return;
    setRunning(dryRun ? 'leadstatus_dryrun' : 'leadstatus');
    setLeadStatusResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/data-repair/backfill-enrolled-lead-status`,
        { dry_run: !!dryRun },
        { withCredentials: true, timeout: 180000 }
      );
      setLeadStatusResult({ ...res.data });
      if (dryRun) {
        toast.info(`Dry-run · ${res.data.leads_matched} lead(s) would be fixed`);
      } else {
        toast.success(`Fixed ${res.data.leads_fixed} lead(s) → Enrolled`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill enrolled lead status failed');
    } finally {
      setRunning(null);
    }
  };

  // V36.8 — Heal lead + booking assignments. Idempotent.
  const runHealAssignments = async () => {
    if (!window.confirm(
      'Backfill assignments on ALL historical leads + bookings?\n\n' +
      '• Every lead without a Pre-Sales owner → round-robin assignment\n' +
      '• Every booking without a Sales owner → inherit from lead OR round-robin\n\n' +
      'Idempotent — already-assigned rows are no-ops. Safe to re-run.'
    )) return;
    setRunning('heal_assignments');
    setAssignResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/data-repair/heal-assignments`, {}, {
        withCredentials: true, timeout: 300000,
      });
      setAssignResult(res.data);
      const unresolved = (res.data.leads_unresolved || 0) + (res.data.bookings_unresolved || 0);
      if (unresolved > 0) {
        toast.warning(`Healed leads=${res.data.leads_healed}, bookings=${res.data.bookings_healed} · ${unresolved} unresolved (no active rep configured)`);
      } else {
        toast.success(`All healed · leads=${res.data.leads_healed}, bookings=${res.data.bookings_healed}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assignment heal failed');
    } finally {
      setRunning(null);
    }
  };

  // Fix +1 phone leads wrongly marked domestic → US / international. Idempotent.
  const runFixPlus1 = async () => {
    if (!window.confirm(
      'Fix all DOMESTIC leads that have a +1 (US/Canada) phone number?\n\n' +
      '• Sets country = United States and section = International\n' +
      '• Only touches leads currently marked domestic with a +1 number\n\n' +
      'Idempotent — safe to re-run.'
    )) return;
    setRunning('fix_plus1');
    setPlus1Result(null);
    try {
      const res = await axios.post(`${API_URL}/api/data-repair/fix-plus1-country`, {}, {
        withCredentials: true, timeout: 300000,
      });
      setPlus1Result(res.data);
      toast.success(`Fixed ${res.data.fixed} lead(s) → United States / International`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fix +1 country failed');
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="card mt-6" data-testid="data-repair-card">
      <div className="card-body">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-indigo-600" />
              Data Integrity
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Daily integrity audit + retro-fixes. The Marketing &amp; Sales dashboards self-correct via webhooks; these are escape-hatches.
            </p>
          </div>
        </div>

        {/* ── Manual integrity reconcile ───────────────────────────────────── */}
        <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <RefreshCw className="w-3.5 h-3.5" />
                Manual Integrity Reconcile
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                Runs the same Razorpay ↔ OPS audit as the daily 9 AM IST cron. Flags drift (missing captures, orphan refunds, misattribution). No business-variance alerts — only data-integrity issues.
              </p>
            </div>
            <button
              onClick={runReconcile}
              disabled={running === 'reconcile'}
              className="px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-lg hover:bg-slate-800 whitespace-nowrap disabled:opacity-50"
              data-testid="reconcile-yesterday-btn"
            >
              {running === 'reconcile' ? 'Running…' : 'Reconcile yesterday'}
            </button>
          </div>
          {reconcileResult && (
            <pre className="text-[10px] mt-3 p-2 bg-slate-900 text-slate-300 rounded overflow-auto max-h-40">
              {JSON.stringify(reconcileResult, null, 2)}
            </pre>
          )}
        </div>

        {/* ── V36.3 — Sync directly from Razorpay API ──────────────────────── */}
        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Download className="w-3.5 h-3.5 text-amber-600" />
                V36.3 · Sync Razorpay Direct Captures (live API pull)
              </h4>
              <p className="text-xs text-slate-600 mt-0.5">
                For payments captured directly on the Razorpay dashboard (no payment_link, no
                webhook). Pulls the Razorpay Payments API and inserts them into OPS with
                amount / currency / captured_at / status / lead link / Pre-Sales attribution.
                Idempotent — re-running on the same window is safe.
              </p>
              <p className="text-[11px] text-amber-700 mt-1 italic">
                Same row-upsert logic as the CSV importer — guaranteed identical output. Use the
                dry-run first to preview how many will be created.
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="text-xs text-slate-600 flex items-center gap-2">
              Look back
              <input
                type="number"
                min={1}
                max={365}
                value={rzpSyncDays}
                onChange={(e) => setRzpSyncDays(e.target.value)}
                className="input-base text-sm w-20 px-2 py-1"
                data-testid="rzp-sync-days"
              />
              day(s)
            </label>
            <button
              onClick={() => runRzpSync(true)}
              disabled={running === 'rzp_dryrun' || running === 'rzp_sync'}
              className="px-3 py-1.5 text-sm font-medium bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 disabled:opacity-50"
              data-testid="rzp-sync-dryrun-btn"
            >
              {running === 'rzp_dryrun' ? 'Checking…' : 'Dry-run (preview)'}
            </button>
            <button
              onClick={() => runRzpSync(false)}
              disabled={running === 'rzp_dryrun' || running === 'rzp_sync'}
              className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              data-testid="rzp-sync-live-btn"
            >
              {running === 'rzp_sync' ? 'Syncing…' : 'Sync now'}
            </button>
          </div>

          {rzpSyncResult && (
            <div className="mt-3" data-testid="rzp-sync-result">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <div className="p-2 bg-white rounded">
                  <div className="text-slate-500">Fetched</div>
                  <div className="font-bold text-slate-900">{rzpSyncResult.fetched_from_razorpay}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className="text-emerald-600">{rzpSyncResult.dry_run ? 'Would create' : 'Created'}</div>
                  <div className="font-bold text-emerald-700">{rzpSyncResult.payments_created}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className="text-indigo-600">Updated</div>
                  <div className="font-bold text-indigo-700">{rzpSyncResult.payments_updated}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className="text-slate-500">Unchanged</div>
                  <div className="font-bold text-slate-700">{rzpSyncResult.payments_unchanged}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className="text-slate-500">Leads relinked</div>
                  <div className="font-bold text-slate-900">{rzpSyncResult.leads_relinked}</div>
                </div>
              </div>
              {rzpSyncResult.dry_run && (
                <div className="mt-2 text-[11px] text-amber-700 italic">
                  Dry-run only — no DB changes were made. Click <strong>Sync now</strong> to apply.
                </div>
              )}
              {rzpSyncResult.skipped_non_terminal > 0 && (
                <div className="mt-1 text-[10px] text-slate-500">
                  Skipped {rzpSyncResult.skipped_non_terminal} row(s) with non-terminal status (created / authorized).
                </div>
              )}
              {rzpSyncResult.errors?.length > 0 && (
                <div className="mt-2 p-2 bg-rose-50 rounded border border-rose-200">
                  <div className="text-rose-600 font-semibold text-[11px]">
                    {rzpSyncResult.errors.length} error(s)
                  </div>
                  <div className="text-[10px] text-rose-700 mt-0.5 max-h-20 overflow-auto">
                    {rzpSyncResult.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Fix +1 phone leads wrongly marked domestic ─ */}
        <div className="mt-4 p-4 bg-sky-50 rounded-lg border border-sky-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <UserCheck className="w-3.5 h-3.5 text-sky-600" />
                Fix +1 Numbers Marked Domestic (one-shot)
              </h4>
              <p className="text-xs text-slate-600 mt-0.5">
                Any lead with a <strong>+1 (US/Canada)</strong> phone that is currently marked <strong>domestic</strong> is
                corrected to <strong>country = United States</strong> and <strong>section = International</strong>. Idempotent.
              </p>
              <p className="text-[11px] text-sky-700 mt-1 italic">
                Going forward, new leads with a +1 number auto-default to United States / International at creation.
              </p>
            </div>
            <button
              onClick={runFixPlus1}
              disabled={running === 'fix_plus1'}
              className="px-3 py-1.5 text-sm font-medium bg-sky-600 text-white rounded-lg hover:bg-sky-700 whitespace-nowrap disabled:opacity-50"
              data-testid="fix-plus1-btn"
            >
              {running === 'fix_plus1' ? 'Fixing…' : 'Fix +1 leads'}
            </button>
          </div>
          {plus1Result && (
            <div className="mt-3 grid grid-cols-3 gap-2 text-xs" data-testid="fix-plus1-result">
              <div className="p-2 bg-white rounded"><div className="text-slate-500">Scanned</div><div className="font-bold text-slate-900">{plus1Result.scanned}</div></div>
              <div className="p-2 bg-white rounded"><div className="text-slate-500">Matched</div><div className="font-bold text-slate-900">{plus1Result.matched}</div></div>
              <div className="p-2 bg-white rounded"><div className="text-sky-600">Fixed</div><div className="font-bold text-sky-700">{plus1Result.fixed}</div></div>
            </div>
          )}
        </div>

        {/* ── V36.8 — Heal Assignments (lead → pre-sales, booking → sales) ─ */}
        <div className="mt-4 p-4 bg-violet-50 rounded-lg border border-violet-200">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <UserCheck className="w-3.5 h-3.5 text-violet-600" />
                V36.8 · Heal Lead + Booking Assignments (one-shot)
              </h4>
              <p className="text-xs text-slate-600 mt-0.5">
                Enforces the canonical invariant: <strong>every lead has a Pre-Sales owner, every booking has a Sales owner</strong>.
                Walks all historical leads/bookings; round-robin assigns by country for any row missing an owner.
                Bookings inherit from the linked lead first, then fall back to round-robin. Idempotent.
              </p>
              <p className="text-[11px] text-violet-700 mt-1 italic">
                Run this once after deploying V36.8 to clean up pre-existing orphans.
                Going forward, the insert hooks in <code>routers/leads.py</code> + <code>routers/calendar.py</code> ensure no new orphans are created.
              </p>
            </div>
            <button
              onClick={runHealAssignments}
              disabled={running === 'heal_assignments'}
              className="px-3 py-1.5 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 whitespace-nowrap disabled:opacity-50"
              data-testid="heal-assignments-btn"
            >
              {running === 'heal_assignments' ? 'Healing…' : 'Heal assignments'}
            </button>
          </div>
          {assignResult && (
            <div className="mt-3" data-testid="heal-assignments-result">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                <div className="p-2 bg-white rounded">
                  <div className="text-slate-500">Leads scanned</div>
                  <div className="font-bold text-slate-900">{assignResult.leads_scanned}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className="text-violet-600">Leads healed</div>
                  <div className="font-bold text-violet-700">{assignResult.leads_healed}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className={`${(assignResult.leads_unresolved || 0) > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                    Leads unresolved
                  </div>
                  <div className={`font-bold ${(assignResult.leads_unresolved || 0) > 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                    {assignResult.leads_unresolved}
                  </div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className="text-slate-500">Bookings scanned</div>
                  <div className="font-bold text-slate-900">{assignResult.bookings_scanned}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className="text-violet-600">Bookings healed</div>
                  <div className="font-bold text-violet-700">{assignResult.bookings_healed}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className={`${(assignResult.bookings_unresolved || 0) > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                    Bookings unresolved
                  </div>
                  <div className={`font-bold ${(assignResult.bookings_unresolved || 0) > 0 ? 'text-rose-700' : 'text-slate-700'}`}>
                    {assignResult.bookings_unresolved}
                  </div>
                </div>
              </div>
              {((assignResult.leads_unresolved || 0) + (assignResult.bookings_unresolved || 0)) > 0 && (
                <div className="mt-2 p-2 bg-rose-50 rounded border border-rose-200 text-[11px] text-rose-700">
                  Some rows could not be assigned — no active Pre-Sales / Sales user is configured for the lead&apos;s country. Activate users under <em>People → Users</em> and re-run.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── V42.5 — Backfill Enrolled lead_status (fix WIP for paid customers) ── */}
        <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <UserCheck className="w-3.5 h-3.5 text-emerald-600" />
              V42.5 · Fix Enrolled Lead Status (one-shot)
            </h4>
            <p className="text-xs text-slate-600 mt-0.5">
              Sets <code className="text-[10px] bg-white px-1 rounded">lead_status = enrolled</code> for every paid
              customer (enrollment_status enrolled/renewed) still stuck on <em>WIP</em> or another pipeline stage.
              Fixes the Sales <strong>Enrolled Leads</strong> list showing WIP for customers who already paid.
              Idempotent — safe to re-run.
            </p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              onClick={() => runBackfillEnrolledStatus(true)}
              disabled={running === 'leadstatus_dryrun' || running === 'leadstatus'}
              className="px-3 py-1.5 text-sm font-medium bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 disabled:opacity-50"
              data-testid="backfill-leadstatus-dryrun-btn"
            >
              {running === 'leadstatus_dryrun' ? 'Checking…' : 'Dry-run (preview)'}
            </button>
            <button
              onClick={() => runBackfillEnrolledStatus(false)}
              disabled={running === 'leadstatus_dryrun' || running === 'leadstatus'}
              className="px-3 py-1.5 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              data-testid="backfill-leadstatus-btn"
            >
              {running === 'leadstatus' ? 'Fixing…' : 'Fix now'}
            </button>
          </div>
          {leadStatusResult && (
            <div className="mt-3" data-testid="backfill-leadstatus-result">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-white rounded">
                  <div className="text-slate-500">Matched</div>
                  <div className="font-bold text-slate-900">{leadStatusResult.leads_matched}</div>
                </div>
                <div className="p-2 bg-white rounded">
                  <div className="text-emerald-600">{leadStatusResult.dry_run ? 'Would fix' : 'Fixed'}</div>
                  <div className="font-bold text-emerald-700">{leadStatusResult.dry_run ? leadStatusResult.leads_matched : leadStatusResult.leads_fixed}</div>
                </div>
              </div>
              {leadStatusResult.dry_run && (
                <div className="mt-2 text-[11px] text-emerald-700 italic">
                  Dry-run only — no changes were made. Click <strong>Fix now</strong> to apply.
                </div>
              )}
              {leadStatusResult.samples?.length > 0 && (
                <div className="mt-2 p-2 bg-white rounded border border-emerald-200">
                  <div className="text-[11px] text-slate-500 mb-1">Sample (first {leadStatusResult.samples.length}):</div>
                  <div className="space-y-1 max-h-32 overflow-auto">
                    {leadStatusResult.samples.map((s, i) => (
                      <div key={i} className="text-[10px] text-slate-700 font-mono">
                        {s.name || s.lead_id} <span className="text-slate-400">[{s.old_status || 'n/a'}]</span> → <span className="text-emerald-700 font-semibold">enrolled</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── One-shot: make every lead phone searchable ─────────────────── */}
        <div className="mt-4 p-4 bg-rose-50 rounded-lg border border-rose-200">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <UserCheck className="w-3.5 h-3.5 text-rose-600" />
              Normalize Lead Phones (one-shot)
            </h4>
            <p className="text-xs text-slate-600 mt-0.5">
              Leads imported from CSV / Meta can store the phone as a <strong>number</strong> or with
              formatting (<code className="text-[10px] bg-white px-1 rounded">+91 99002 51009</code>), which makes them
              invisible to phone search. This rewrites them to clean strings and backfills{' '}
              <code className="text-[10px] bg-white px-1 rounded">normalized_phone</code>, so search, dedupe, merge and
              WhatsApp all agree. Idempotent — safe to re-run.
            </p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              onClick={() => runNormalizePhones(true)}
              disabled={running === 'phones_dryrun' || running === 'phones'}
              className="px-3 py-1.5 text-sm font-medium bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 disabled:opacity-50"
              data-testid="normalize-phones-dryrun-btn"
            >
              {running === 'phones_dryrun' ? 'Checking…' : 'Dry-run (preview)'}
            </button>
            <button
              onClick={() => runNormalizePhones(false)}
              disabled={running === 'phones_dryrun' || running === 'phones'}
              className="px-3 py-1.5 text-sm font-medium bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50"
              data-testid="normalize-phones-btn"
            >
              {running === 'phones' ? 'Fixing…' : 'Normalize phones'}
            </button>
          </div>
          {phoneResult && (
            <div className="mt-3" data-testid="normalize-phones-result">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                <div className="p-2 bg-white rounded"><div className="text-slate-500">Scanned</div><div className="font-bold text-slate-900">{phoneResult.scanned}</div></div>
                <div className="p-2 bg-white rounded"><div className="text-rose-600">Numeric fixed</div><div className="font-bold text-rose-700">{phoneResult.numeric_phones_fixed}</div></div>
                <div className="p-2 bg-white rounded"><div className="text-slate-500">Trimmed</div><div className="font-bold text-slate-700">{phoneResult.formatting_trimmed}</div></div>
                <div className="p-2 bg-white rounded"><div className="text-indigo-600">Normalized filled</div><div className="font-bold text-indigo-700">{phoneResult.normalized_phone_filled}</div></div>
                <div className="p-2 bg-white rounded"><div className="text-slate-500">Total changed</div><div className="font-bold text-slate-900">{phoneResult.total_changed}</div></div>
              </div>
              {phoneResult.dry_run && (
                <div className="mt-2 text-[11px] text-rose-700 italic">
                  Dry-run only — nothing was written. Click <strong>Normalize phones</strong> to apply.
                </div>
              )}
              {phoneResult.samples?.length > 0 && (
                <div className="mt-2 p-2 bg-white rounded border border-rose-200 space-y-1 max-h-32 overflow-auto">
                  {phoneResult.samples.map((s, i) => (
                    <div key={i} className="text-[10px] text-slate-700 font-mono">
                      {s.name || s.lead_id}: <span className="text-slate-400">{String(s.before)}</span> → <span className="text-rose-700 font-semibold">{String(s.after)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── V57.2: India funnel / INR-paid leads wrongly tagged International ── */}
        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <UserCheck className="w-3.5 h-3.5 text-amber-600" />
              Fix India leads tagged "International"
            </h4>
            <p className="text-xs text-slate-600 mt-0.5">
              Leads from <strong>soulsanskar.in</strong> (India-only ads) or who <strong>paid in INR</strong> but were
              tagged <em>International</em> because our IP lookup could not place them. These are the rows behind the
              LMS <em>"International lead paid in INR"</em> audit emails. Sets country = India and pushes the
              correction to the LMS. A real foreign country is never overwritten. Idempotent.
            </p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              onClick={() => runHealInFunnel(true)}
              disabled={running === 'infunnel_dryrun' || running === 'infunnel'}
              className="px-3 py-1.5 text-sm font-medium bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 disabled:opacity-50"
              data-testid="heal-in-funnel-dryrun-btn"
            >
              {running === 'infunnel_dryrun' ? 'Checking…' : 'Dry-run (preview)'}
            </button>
            <button
              onClick={() => runHealInFunnel(false)}
              disabled={running === 'infunnel_dryrun' || running === 'infunnel'}
              className="px-3 py-1.5 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              data-testid="heal-in-funnel-btn"
            >
              {running === 'infunnel' ? 'Fixing…' : 'Fix country → India'}
            </button>
          </div>
          {inFunnelResult && (
            <div className="mt-3" data-testid="heal-in-funnel-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="p-2 bg-white rounded"><div className="text-slate-500">Scanned</div><div className="font-bold text-slate-900">{inFunnelResult.scanned}</div></div>
                <div className="p-2 bg-white rounded"><div className="text-slate-500">Eligible</div><div className="font-bold text-slate-700">{inFunnelResult.eligible}</div></div>
                <div className="p-2 bg-white rounded"><div className="text-amber-600">Set to India</div><div className="font-bold text-amber-700">{inFunnelResult.fixed}</div></div>
                <div className="p-2 bg-white rounded"><div className="text-indigo-600">Pushed to LMS</div><div className="font-bold text-indigo-700">{inFunnelResult.lms_pushed}</div></div>
              </div>
              {inFunnelResult.dry_run && (
                <div className="mt-2 text-[11px] text-amber-700 italic">
                  Dry-run only — nothing was written. Click <strong>Fix country → India</strong> to apply.
                </div>
              )}
              {inFunnelResult.samples?.length > 0 && (
                <div className="mt-2 p-2 bg-white rounded border border-amber-200 space-y-1 max-h-32 overflow-auto">
                  {inFunnelResult.samples.map((s, i) => (
                    <div key={i} className="text-[10px] text-slate-700 font-mono">
                      {s.name || s.lead_id}: <span className="text-slate-400">{s.old_country || 'blank'}</span> → <span className="text-amber-700 font-semibold">India</span> <span className="text-slate-400">({s.reason})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default DataRepairCard;
